import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

export interface MessageAttachmentMeta {
  id: string;
  filename: string;
  mime: string;
  kind: 'image' | 'pdf' | 'docx' | 'txt' | 'other';
  ai_summary?: string | null;
}

export interface AxisMessageMetadata {
  attachments?: MessageAttachmentMeta[];
  plan?: any;
  draft?: any;
  report?: any;
  [k: string]: any;
}

export interface AxisMessage {
  id: string;
  role: 'user' | 'axis' | 'system';
  content: string;
  message_type: string;
  metadata?: AxisMessageMetadata | null;
  created_at: string;
  failed?: boolean;
}

interface ChatState {
  messages: AxisMessage[];
  loading: boolean;
  lastFetched: number;
  briefRequestedAt: number;
  ownerUserId: string | null;
  setMessages: (messages: AxisMessage[]) => void;
  addMessage: (msg: AxisMessage) => void;
  replaceTemp: (tempId: string, confirmed: AxisMessage, axisMsg: AxisMessage) => void;
  removeTemp: (tempId: string) => void;
  markFailed: (tempId: string) => void;
  removeMessage: (id: string) => void;
  addBriefMessage: (msg: AxisMessage) => void;
  setLoading: (loading: boolean) => void;
  markFetched: (userId?: string | null) => void;
  markBriefRequested: () => void;
  clearChat: () => void;
}

export const useChatStore = create<ChatState>()(
  persist(
    (set) => ({
      messages: [],
      loading: false,
      lastFetched: 0,
      briefRequestedAt: 0,
      ownerUserId: null,

      setMessages: (messages) => set({ messages }),
      addMessage: (msg) => set((state) => ({ messages: [...state.messages, msg] })),
      replaceTemp: (tempId, confirmed, axisMsg) =>
        set((state) => ({
          messages: [
            ...state.messages.filter((m) => m.id !== tempId),
            confirmed,
            axisMsg,
          ],
        })),
      removeTemp: (tempId) =>
        set((state) => ({
          messages: state.messages.filter((m) => m.id !== tempId),
        })),
      markFailed: (tempId) =>
        set((state) => ({
          messages: state.messages.map((m) =>
            m.id === tempId ? { ...m, failed: true } : m
          ),
        })),
      removeMessage: (id) =>
        set((state) => ({
          messages: state.messages.filter((m) => m.id !== id),
        })),
      addBriefMessage: (msg) =>
        set((state) => ({
          messages: state.messages.some((m) => m.message_type === 'brief')
            ? state.messages
            : [...state.messages, msg],
        })),
      setLoading: (loading) => set({ loading }),
      markFetched: (userId) =>
        set((state) => ({
          lastFetched: Date.now(),
          ownerUserId: userId !== undefined ? userId : state.ownerUserId,
        })),
      markBriefRequested: () => set({ briefRequestedAt: Date.now() }),
      clearChat: () =>
        set({
          messages: [],
          lastFetched: 0,
          briefRequestedAt: 0,
          ownerUserId: null,
          loading: false,
        }),
    }),
    {
      name: 'axis-chat',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        // Cap persisted history at the most recent 200 messages to stay
        // well under iOS Safari's ~5MB per-origin localStorage quota.
        // Older messages still live on the server and are rehydrated on
        // the next fetch.
        messages: state.messages.slice(-200),
        lastFetched: state.lastFetched,
        briefRequestedAt: state.briefRequestedAt,
        ownerUserId: state.ownerUserId,
      }),
      // v2: introduced ownerUserId for cross-account safety. Bumping the
      // version evicts any legacy persisted chat that pre-dates the field
      // (where ownerUserId would be null) so a user on a shared device
      // never briefly sees the previous account's messages on first load.
      version: 2,
    }
  )
);
